import { supabase } from './supabase';
import { UserTier, UserUsage, UsageLimits, TierInfo, UserTierWithBilling } from '../types';
import { billingService } from './billingService';

// Prosumer launch flag. LIVE as of 2026-07-18 — live Stripe Prosumer prices
// ($19/mo, $182/yr) and edge-fn price IDs are in place, so the tier is on by
// default and Free drops to 2/mo. Kept as an env-controlled kill switch: set
// VITE_ENABLE_PROSUMER='false' to hide the tier AND restore Free's 5/mo.
export const PROSUMER_ENABLED = import.meta.env?.VITE_ENABLE_PROSUMER !== 'false';
const FREE_SUMMARY_LIMIT = PROSUMER_ENABLED ? 2 : 5;

export class UserTierService {
  private static instance: UserTierService;

  static getInstance(): UserTierService {
    if (!UserTierService.instance) {
      UserTierService.instance = new UserTierService();
    }
    return UserTierService.instance;
  }

  // Helper method to get authenticated user (tries both getUser() and getSession())
  private async getAuthenticatedUser(): Promise<any> {
    // First try getUser()
    const { data: { user: userFromGetUser } } = await supabase.auth.getUser();
    if (userFromGetUser) {
      return userFromGetUser;
    }
    
    // Fallback to getSession() if getUser() doesn't work
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      return session.user;
    }
    
    return null;
  }

  // Get current user's tier
  async getUserTier(): Promise<string> {
    const user = await this.getAuthenticatedUser();
    
    if (!user) {
      console.log('No authenticated user found');
      return 'anonymous';
    }

    try {
      const { data, error } = await supabase
        .rpc('get_or_create_user_tier', { user_uuid: user.id });

      if (error) {
        console.error('Error getting user tier:', error);
        // On ANY RPC error (not just 409/23505 conflicts), fall back to reading
        // the tier directly from the table before defaulting to 'free' — otherwise
        // a transient RPC failure shows a paying customer the Free badge.
        try {
          const { data: tierData, error: tierError } = await supabase
            .from('user_tiers')
            .select('tier')
            .eq('user_id', user.id)
            .single();

          if (!tierError && tierData?.tier) {
            return tierData.tier;
          }
        } catch (retryError) {
          console.error('Error retrying tier fetch:', retryError);
        }

        // For authenticated users, return 'free' as default instead of 'anonymous'
        return 'free';
      }

      return data || 'free';
    } catch (error) {
      console.error('Exception getting user tier:', error);
      // For authenticated users, return 'free' as default instead of 'anonymous'
      return 'free';
    }
  }

  // Get user tier with billing information
  async getUserTierWithBilling(): Promise<UserTierWithBilling | null> {
    const user = await this.getAuthenticatedUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('user_tiers')
      .select('*')
      .eq('user_id', user.id)
      .single();

    if (error) {
      console.error('Error getting user tier with billing:', error);
      return null;
    }

    return data as UserTierWithBilling;
  }

  // Check if billing is enabled for current user
  async isBillingEnabled(): Promise<boolean> {
    const tierWithBilling = await this.getUserTierWithBilling();
    return tierWithBilling?.billing_enabled || false;
  }

  // Get Comment Summary usage for current month
  async getCommentSummaryUsage(): Promise<number> {
    const user = await this.getAuthenticatedUser();
    return await billingService.getCurrentMonthUsage(user?.id || '');
  }

  // Get current user's usage for this month
  async getUserUsage(): Promise<UserUsage | null> {
    const user = await this.getAuthenticatedUser();
    
    if (!user) {
      return null;
    }

    const { data, error } = await supabase
      .rpc('get_or_create_user_usage', { user_uuid: user.id });

    if (error) {
      console.error('Error getting user usage:', error);
      return null;
    }

    // Map the database response to the expected UserUsage format
    // Database returns: searches_count, downloads_count, summaries_count, comment_summaries_count
    // Frontend expects: searches_count, downloads_count, summaries_count, comment_summaries_count
    if (data && data.length > 0) {
      const usage = data[0];
      return {
        id: '', // We don't have this from the function
        user_id: user.id,
        month_year: new Date().toISOString().slice(0, 7), // YYYY-MM format
        searches_count: usage.searches_count || 0,
        downloads_count: usage.downloads_count || 0,
        summaries_count: usage.summaries_count || 0,
        comment_summaries_count: usage.comment_summaries_count || 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
    }

    return null;
  }

  // Check if user can perform an action
  //
  // For 'summary': delegate to the backend's check_summary_limits via the
  // /subscription/status endpoint. The backend is the gating authority — it
  // re-runs the same check inside /create_personalized_summary and returns
  // 403 if exceeded — so the frontend MUST use the same source of truth.
  // The previous implementation called increment_user_usage (a Supabase
  // RPC), which uses different counters/rules and could OK a request the
  // backend then rejected, leaving the user staring at "Summary generation
  // started" forever.
  //
  // For 'search' / 'download': keep the existing RPC; those are tracked
  // and gated client-side and aren't the source of the mismatch.
  async canPerformAction(action: 'search' | 'download' | 'summary'): Promise<boolean> {
    const user = await this.getAuthenticatedUser();

    if (!user) {
      // Anonymous users — let the page-level UI handle gating.
      return true;
    }

    if (action === 'summary') {
      try {
        const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined)
          ?? 'https://fcc-summarize-app-r4ac3.ondigitalocean.app';
        // Send the user's JWT — the endpoint now verifies ownership. The soft
        // fail-open below stays intentional: the real quota gate is
        // /create_personalized_summary server-side.
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(`${apiBase}/subscription/status/${user.id}`, {
          headers: { 'Authorization': `Bearer ${session?.access_token ?? ''}` },
        });
        if (!res.ok) {
          // If the backend is unreachable, fail open so we don't block the
          // user on a transient outage. /create_personalized_summary will
          // still gate them properly and surface the error via the new
          // job-poll path.
          console.warn(`[userTierService] subscription/status returned ${res.status}; failing open`);
          return true;
        }
        const body = await res.json();
        const canCreate = body?.limits?.can_create;
        // Default to true on a malformed response so we don't lock users out.
        return typeof canCreate === 'boolean' ? canCreate : true;
      } catch (err) {
        console.warn('[userTierService] subscription/status failed; failing open', err);
        return true;
      }
    }

    const { data, error } = await supabase
      .rpc('increment_user_usage', {
        user_uuid: user.id,
        action_type: action,
      });

    if (error) {
      console.error('Error checking action permission:', error);
      return false;
    }

    return data;
  }

  // Get limits for a specific tier
  getTierLimits(tier: string): UsageLimits {
    const limits = {
      // Docket search and bulk download are free for everyone (per pricing page).
      // Only AI comment summaries are gated.
      anonymous: { searches: -1, downloads: -1, summaries: 2 },
      free: { searches: -1, downloads: -1, summaries: FREE_SUMMARY_LIMIT },
      prosumer: { searches: -1, downloads: -1, summaries: 5 },
      pro: { searches: -1, downloads: -1, summaries: -1 },
      enterprise: { searches: -1, downloads: -1, summaries: -1 },
      paid: { searches: -1, downloads: -1, summaries: -1 } // back-compat alias
    };
    return limits[tier as keyof typeof limits] || limits.anonymous;
  }

  // Personal watchlist follow cap. Mirrors the DB trigger enforce_docket_follow_limit:
  // free/anonymous follow 5; prosumer 25; pro/paid/enterprise unlimited (-1).
  static readonly WATCHLIST_FREE_LIMIT = 5;
  static readonly WATCHLIST_PROSUMER_LIMIT = 25;
  getWatchlistLimit(tier: string): number {
    switch (tier) {
      case 'pro':
      case 'enterprise':
      case 'paid':
        return -1;
      case 'prosumer':
        return UserTierService.WATCHLIST_PROSUMER_LIMIT;
      default:
        return UserTierService.WATCHLIST_FREE_LIMIT;
    }
  }

  // Get tier information for display
  getTierInfo(): TierInfo[] {
    return [
      {
        name: 'Free',
        price: '$0/month',
        limits: { searches: -1, downloads: -1, summaries: FREE_SUMMARY_LIMIT },
        features: [
          `${FREE_SUMMARY_LIMIT} AI comment summaries/month`,
          'Follow up to 5 dockets, with alerts',
          'Unlimited docket search',
          'Bulk filing download'
        ]
      },
      ...(PROSUMER_ENABLED ? [{
        name: 'Prosumer',
        price: '$19/month',
        limits: { searches: -1, downloads: -1, summaries: 5 },
        features: [
          '5 cited AI comment summaries/month',
          'Follow up to 25 dockets, with alerts',
          'Research tool (limited)',
          'Everything in Free'
        ],
        popular: true
      }] : []),
      {
        name: 'Pro',
        price: '$199/month',
        limits: { searches: -1, downloads: -1, summaries: -1 },
        features: [
          'Unlimited AI comment summaries',
          'Unlimited watched dockets',
          PROSUMER_ENABLED ? 'Everything in Prosumer' : 'Everything in Free',
          'Workspaces & team collaboration',
          'Full research tool',
          'Priority summary processing',
          'Email support'
        ]
      }
    ];
  }

  // Get usage percentage for a specific action
  async getUsagePercentage(action: 'search' | 'download' | 'summary'): Promise<number> {
    const tier = await this.getUserTier();
    const usage = await this.getUserUsage();
    const limits = this.getTierLimits(tier);

    if (!usage || limits[`${action}s` as keyof UsageLimits] === -1) {
      return 0; // Unlimited
    }

    const current = usage[`${action}s_count` as keyof UserUsage] as number;
    const limit = limits[`${action}s` as keyof UsageLimits] as number;
    
    return Math.min((current / limit) * 100, 100);
  }

  // Check if user is approaching limits
  async isApproachingLimits(action: 'search' | 'download' | 'summary'): Promise<boolean> {
    const percentage = await this.getUsagePercentage(action);
    return percentage >= 80;
  }

  // Check if user has hit limits
  async hasHitLimits(action: 'search' | 'download' | 'summary'): Promise<boolean> {
    const percentage = await this.getUsagePercentage(action);
    return percentage >= 100;
  }
}

export const userTierService = UserTierService.getInstance();
